import React, { useState, useEffect } from "react";
import { Col, Row, Table } from "react-bootstrap";
import "bootstrap-icons/font/bootstrap-icons.css";
import "./ReportClassTable.css";
import { getNotes } from "../services/APIClient";

const dummyHeaderArray = [
  "Base",
  "Mid",
  "EOY",
  "Overall Score Observations",
  "Literacy Observations",
  "AEN Observations",
  "Classroom Support Observations",
  "EAL",
  "Watchlist",
  "Attendance Affecting Scores",
];

function ExpandedHandoverView(props) {
  const {
    students,
    showAtRiskOnly,
    setShowAtRiskOnly,
    teachers,
    classid,
    studentsReadingScore,
    onSortData,
    showSortedData,
    getReport,
    onReady,
    noteType = null,
  } = props;

  const [sortCriteria, setSortCriteria] = useState({ field: "name", order: "asc" });
  const [sortedData, setSortedData] = useState([]);
  const [combinedData, setCombinedData] = useState([]);
  const [studentNotes, setStudentNotes] = useState({});
  const [notesReady, setNotesReady] = useState(false);
  const emptyAssessmentScores = { baseline: null, midpoint: null, endOfYear: null };

  useEffect(() => {
    if (!studentsReadingScore || studentsReadingScore.length === 0) return;
    Promise.all(
      studentsReadingScore.map(async (student) => {
        try {
          const report = await getReport(student.id).catch(() => null);
          return {
            ...student,
            ...(report || {}),
            id: student.id,
            assessmentScores: student.assessmentScores || { ...emptyAssessmentScores },
          };
        } catch {
          return { ...student, id: student.id, assessmentScores: student.assessmentScores || { ...emptyAssessmentScores } };
        }
      })
    ).then((combined) => setCombinedData(combined));
  }, [studentsReadingScore, getReport]);

  useEffect(() => {
    const sorted = sortStudents(combinedData, sortCriteria);
    setSortedData(sorted);
    onSortData(sorted);
  }, [combinedData, sortCriteria]);

  useEffect(() => {
    if (combinedData.length === 0) return;
    const notesObj = {};
    Promise.all(
      combinedData.map(async (student) => {
        try {
          const res = await getNotes("student", student.id, noteType);
          notesObj[student.id] = res?.data || [];
        } catch {
          notesObj[student.id] = [];
        }
      })
    ).then(() => {
      setStudentNotes({ ...notesObj });
      setNotesReady(true);
    });
  }, [combinedData, noteType]);

  useEffect(() => {
    if (notesReady) {
      onReady?.();
      setNotesReady(false);
    }
  }, [notesReady, studentNotes]);

  const sortStudents = (students, criteria) =>
    [...students].sort((a, b) => {
      if (criteria.field === "name") {
        return criteria.order === "asc"
          ? a.name?.localeCompare(b.name)
          : b.name?.localeCompare(a.name);
      } else if (criteria.field === "readingScore") {
        const scoreA = a.readingScore === "No completed tasks" || a.readingScore === "NA" ? -1 : Number(a.readingScore);
        const scoreB = b.readingScore === "No completed tasks" || b.readingScore === "NA" ? -1 : Number(b.readingScore);
        return criteria.order === "asc" ? scoreA - scoreB : scoreB - scoreA;
      }
    });

  const handleSortChange = (field) => {
    setSortCriteria((prev) => ({
      field,
      order: prev.field === field && prev.order === "asc" ? "desc" : "asc",
    }));
  };

  const getReadingScoreClass = (score) => {
    if (score <= 20) return "low-color";
    if (score <= 40) return "med-color";
    if (score <= 80) return "noRisk-color";
    if (score <= 100) return "high-color";
    return "";
  };

  const renderAssessmentScore = (score) => {
    if (score === null || score === undefined || score === "No completed tasks" || score === "No assessments") {
      return <span className="reading-score">NYS</span>;
    }

    if (typeof score === "number") {
      return <span className={`reading-score ${getReadingScoreClass(score)}`}>{score}%</span>;
    }

    return <span className="reading-score">{score}</span>;
  };

  const getTeacherName = (teacherId) => {
    const teacher = teachers?.find((t) => t.id === teacherId);
    return teacher?.name || "Unknown";
  };

  const formatNoteDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleString("en-IE", {
        day: "numeric", month: "short", year: "numeric",
        hour: "numeric", minute: "2-digit", hour12: true, timeZone: "Europe/Dublin",
      });
    } catch {
      return dateStr;
    }
  };

  const formatList = (items) => (
    <>
      {items?.map((item, index) => (
        <div key={index} className="item-list">{item}</div>
      ))}
    </>
  );

  const dataToDisplay = sortedData;

  return (
    <Row>
      <Col>
        <div className="table-responsive">
          <Table className="custom-table handover-score-table expanded-handover-table">
            <thead>
              <tr className="table-row">
                <th className="table-header-cell" onClick={() => handleSortChange("name")}>
                  <div className="header-content">Name</div>
                </th>
                {/* <th className="table-header-cell">
                  <div className="header-content">Overall Standard Score vs Test</div>
                </th> */}
                {dummyHeaderArray.map((header, index) => {
                  const scoreStyle =
                    header === "Base" || header === "Mid" || header === "EOY"
                      ? { width: '50px', backgroundColor: '#eef2ff' }
                      : {};
                  return (
                    <th key={index} className="table-header-cell" style={scoreStyle}>
                      <div className="header-content">{header}</div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {dataToDisplay
                ?.filter(
                  (student) =>
                    !showAtRiskOnly ||
                    (student.readingScore !== undefined &&
                      student.readingScore !== null &&
                      student.readingScore <= 40)
                )
                .map((report) => (
                  <React.Fragment key={report.id}>
                    <tr className="table-row student-results-table-row">
                      <td>{report.name}</td>
                      {/* <td className="centre-student-items task-badge-parent">
                        {report.readingScore !== undefined && report.readingScore !== null ? (
                          report.readingScore === "No completed tasks" ? (
                            <span className="reading-score">{report.readingScore}</span>
                          ) : (
                            <span className={`reading-score ${getReadingScoreClass(report.readingScore)}`}>
                              {report.readingScore}%
                            </span>
                          )
                        ) : null}
                      </td> */}
                      <td className="centre-student-items task-badge-parent score-individual-cell" style={{ width: '50px' }}>{renderAssessmentScore(report.assessmentScores?.baseline)}</td>
                      <td className="centre-student-items task-badge-parent score-individual-cell" style={{ width: '50px' }}>{renderAssessmentScore(report.assessmentScores?.midpoint)}</td>
                      <td className="centre-student-items task-badge-parent score-individual-cell" style={{ width: '50px' }}>{renderAssessmentScore(report.assessmentScores?.endOfYear)}</td>
                      <td>{formatList(report.readingscorenotes)}</td>
                      <td>{report.literacyobservations}</td>
                      <td>{formatList(report.additionalneeds)}</td>
                      <td>{formatList(report.supportplan)}</td>
                      <td>{report.eal ? "Yes" : "No"}</td>
                      <td>{report.sen ? "Yes" : "No"}</td>
                      <td>{report.attendanceaffectingscores ? "Yes" : "No"}</td>
                    </tr>
                    {(studentNotes[report.id] || []).length > 0 && (
                      <tr>
                        <td colSpan={11} style={{ background: "#f8f9fa", padding: "8px 16px 12px" }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 6 }}>
                            {report.name} — Notes
                          </div>
                          {(studentNotes[report.id] || []).map((n) => (
                            <div
                              key={n.id}
                              style={{ marginBottom: 8, padding: "8px 12px", borderLeft: "3px solid #1a56db", background: "white", borderRadius: 4 }}
                            >
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                                {/* <span style={{ fontSize: 11, fontWeight: "bold", background: "#1a56db", color: "white", padding: "2px 8px", borderRadius: 4 }}>
                                  {n.note_type}
                                </span> */}
                                <span style={{ fontSize: 11, color: "#666" }}>
                                  {getTeacherName(n.teacherid)} &bull; {formatNoteDate(n.date_created)}
                                </span>
                              </div>
                              <div style={{ fontSize: 13, color: "#333", whiteSpace: "pre-wrap" }}>{n.note_text}</div>
                            </div>
                          ))}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
            </tbody>
          </Table>
        </div>
      </Col>
    </Row>
  );
}

export default ExpandedHandoverView;
